import React from "react";

import { useDispatch, useSelector } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import BodyText from "Cobalt/BodyText";
import Button from "Cobalt/Button";
import { Modal, ModalHeader, ModalBody, ModalTitle, ModalFooter, openModal, closeModal } from "Cobalt/Modal";
import Icon from "Common/Icon";
import { usePartnerContext } from "Common/PartnerContext";

import { HOW_DO_I_GET_MODAL_NAME, DONT_HAVE_MODAL_NAME } from "./constants";
import { getModalName, useModalDescription, useDocumentPickerContext, bem } from "./utils";

const PARTNER_INSTRUCTIONS_LINKS = {
  "square,state_wage_report": "https://support.simplyinsured.com/s/article/How-can-I-find-the-wage-report-that-you-are-requesting-for-my-application-in-Square",
  "square,payroll_report":
    "https://support.simplyinsured.com/s/article/How-can-I-find-the-payroll-report-that-you-are-requesting-as-part-of-my-application-in-Square",
  "intuit,state_wage_report":
    "https://support.simplyinsured.com/s/article/How-can-I-find-the-wage-report-that-you-are-requesting-for-my-application-on-QuickBooks",
  "intuit,payroll_report":
    "https://support.simplyinsured.com/s/article/How-can-I-find-the-payroll-report-that-you-are-requesting-as-part-of-my-application-on-QuickBooks",
  "primepay,state_wage_report":
    "https://support.simplyinsured.com/s/article/How-can-I-find-the-wage-report-that-you-are-requesting-for-my-application-on-PrimePay",
};

const HowDoIGetThisModal = () => {
  const { description, fileInputName, index, sampleDocuments, title } = useDocumentPickerContext();
  const modalDescription = useModalDescription({
    fileInputName,
    description,
    title,
  });

  const currentlyOpenModal = useSelector((state) => state.openModal);
  const dispatch = useDispatch();
  const modalName = getModalName(HOW_DO_I_GET_MODAL_NAME, index);

  const handleCloseModal = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      label: "done",
      location: "how do I get this?",
      page: "document_upload",
    });

    dispatch(closeModal(modalName));
  };

  return (
    <Modal
      show={currentlyOpenModal === modalName}
      onHide={handleCloseModal}
      aria-hidden={currentlyOpenModal !== modalName}
      data-testid="HowDoIGetThisModal"
      data-value={fileInputName}
    >
      <ModalHeader closeButton>
        <ModalTitle>{title}</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <BodyText mb={4}>{modalDescription}</BodyText>
        {sampleDocuments.length > 0 && (
          <ul>
            {sampleDocuments.map((doc, i) => (
              <li key={i}>
                <Button color="link" href={doc.url} target="_blank" className={bem("SampleDocumentLink")}>
                  Sample {doc.name}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={handleCloseModal}>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const HowDoIGetThisTrigger = () => {
  const dispatch = useDispatch();
  const { docType, index } = useDocumentPickerContext();
  const { partner } = usePartnerContext();
  const instructionsLink = PARTNER_INSTRUCTIONS_LINKS[`${partner},${docType}`];
  const handleClickHowDoIGetThis = () => {
    AnalyticsTracking.trackEvent("document_upload_how_do_I_get_this_clicked", {});

    dispatch(openModal(getModalName(HOW_DO_I_GET_MODAL_NAME, index)));
  };
  if (instructionsLink) {
    return (
      <Button color="link" className={bem("InfoLink")} href={instructionsLink} target="_blank">
        How do I get this?&nbsp;
        <Icon icon="external-link-alt" size="xs" />
      </Button>
    );
  }
  return (
    <Button color="link" className={bem("InfoLink")} onClick={handleClickHowDoIGetThis}>
      How do I get this?
    </Button>
  );
};

const HowDoIGetThis = () => {
  const {
    mainDocumentState: { fullfilled },
  } = useDocumentPickerContext();

  return fullfilled ? null : (
    <>
      <HowDoIGetThisTrigger />
      <br />
      <HowDoIGetThisModal />
    </>
  );
};

export default HowDoIGetThis;
